import React, {
  ReactNode,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react'

import dayjs from 'dayjs'
import Cookies from 'js-cookie'

import { callApiNew } from 'api/axios'
import { PATHS } from 'api/paths'

import { BubbleChat, BubbleChatProps } from './bubbleChat'
import { AIChatBotContext } from './context'
import { Widget } from './widget'
import { MessageProps } from './widget/messageList'

export type AIChatBotProps = {
  name: string
  avatar: string
  cookieId?: string
  allowSaveHistory?: boolean
  children?: ReactNode
  showHighlightMessage?: boolean
  numberToShowHighlightMess?: number
  onClickHighlightMess?: () => void
  widgetModalProps?: BubbleChatProps['widgetModalProps']
  starterAvatarSize?: number
  isShortVer?: boolean
  autoPopupTimes?: number
}

export type MesageApiProp = {
  role: 'BOT' | 'USER'
  content: string
  created_at: string
}

export type Props = AIChatBotProps

export type HandlerProps = {
  resetChatId: () => void
  setShowWidget: (state: boolean) => void
}

const forwardRefEle = forwardRef(function ForwardRefEle(
  {
    name,
    avatar,
    children,
    cookieId = 'aiChatId',
    allowSaveHistory,
    numberToShowHighlightMess = 0,
    showHighlightMessage,
    onClickHighlightMess,
    widgetModalProps,
    starterAvatarSize,
    isShortVer,
    autoPopupTimes = 2,
  }: Props,
  ref,
) {
  const [chatId, setChatId] = useState<string>('')
  const [messageList, setMessageList] = useState<MessageProps[] | null>(null)
  const [showWidget, setShowWidget] = useState(false)
  const [expandWidget, setExpandWidget] = useState(!Boolean(isShortVer))

  useImperativeHandle(ref, () => ({
    resetChatId: () => {
      if (allowSaveHistory) {
        setChatId('')
        Cookies.remove(cookieId)
      }
      setMessageList(null)
    },
    setShowWidget,
  }))

  const fetchHistory = useCallback(async (existChatId: string) => {
    const res = await callApiNew(
      PATHS.CHATBOT.GET_CHAT_HISTORY,
      {
        method: 'GET',
        params: {
          project: 'sgm-soya-chat',
          userId: Cookies.get('_ga'),
          chatId: existChatId,
        },
      },
      'chatbot',
    )
    const historyList = (res?.history || []) as MesageApiProp[]
    if (historyList.length > 0) {
      setChatId(existChatId)
      setMessageList(
        historyList.map((i) => ({
          type: i.role === 'BOT' ? 'incoming' : 'current',
          message: i.content,
          date: dayjs(i.created_at).toDate(),
        })),
      )
    } else {
      Cookies.remove(cookieId)
    }
  }, [])

  useEffect(() => {
    if (allowSaveHistory) {
      const existChatId = Cookies.get(cookieId)
      existChatId && fetchHistory(existChatId)
    }
  }, [])

  return (
    <AIChatBotContext.Provider
      value={{
        name,
        avatar,
        cookieId,
        fetchHistory,
        messageList,
        setMessageList,
        chatId,
        setChatId,
        numberToShowHighlightMess,
        showHighlightMessage,
        onClickHighlightMess,
        starterAvatarSize,
        showWidget,
        setShowWidget,
        isShortVer,
        expandWidget,
        setExpandWidget,
        autoPopupTimes,
      }}
    >
      {children || <BubbleChat widgetModalProps={widgetModalProps} />}
    </AIChatBotContext.Provider>
  )
})

interface CompoundedComponent
  extends React.ForwardRefExoticComponent<
    Props & { ref: React.Ref<HandlerProps> }
  > {
  Widget: typeof Widget
  BubbleChat: typeof BubbleChat
}

const AIChatBot = ({
  ...forwardRefEle,
  Widget: Widget,
  BubbleChat: BubbleChat,
} as unknown) as CompoundedComponent

export default AIChatBot
